import { Button, Collapse, Flex, Row, Tag, Typography } from 'antd';
import ReactECharts from 'echarts-for-react';
import he from 'he';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useThemeSummary } from '../../api/CodeFrames';
import { useQuestionResponses } from '../../api/Questions';
import { usePersistedSegment } from '../../api/Segments';
import { useQuestions, useSurvey } from '../../api/Surveys';
import { useThemes } from '../../api/Themes';
import BackButton from '../../components/BackButton';
import DisplayAttributesModal from '../../components/DisplayAttributesModal';
import Loading from '../../components/Loading';
import SearchableTable from '../../components/SearchableTable';
import { SurveyUploadType } from '../../constants';
import { getThemeSentimentBarChart } from '../../echarts/ChartHelper';
import useParams from '../../hooks/useParams';
import { Gray4, White } from '../../styles';
import ThemeSummary from './ThemeSummary';

const { Text, Title } = Typography;

const StyledDiv = styled.div`
  padding: 0 24px 24px;
`;

const StyledCollapse = styled(Collapse)`
  background: ${White};
  border: 1px solid ${Gray4};

  .ant-collapse-content {
    border: none;
  }

  .ant-collapse-item {
    border: none;
  }
`;

const StyledSearchableTable = styled(SearchableTable)`
  .ant-table-tbody > tr > td {
    border: none;
  }
`;

const StyledResponseText = styled(Text)`
  margin-right: 8px;
`;

function ResponseExplorer() {
  const navigate = useNavigate();
  const { questionId, themeId, surveyId } = useParams();
  const [searchParams] = useSearchParams();

  const [searchFilters, setSearchFilters] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [positiveCount, setPositiveCount] = useState(0);
  const [negativeCount, setNegativeCount] = useState(0);
  const [neutralCount, setNeutralCount] = useState(0);
  const [attributesModalVisible, setAttributesModalVisible] = useState(false);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState([]);

  const DISPLAY_ATTRIBUTES_CACHE_KEY = `displayAttributes${surveyId}`;

  const setPercentages = (responses) => {
    setPositiveCount(
      responses.reduce((acc, cur) => (cur.sentiment === 'positive' ? acc + 1 : acc), 0),
    );

    setNegativeCount(
      responses.reduce((acc, cur) => (cur.sentiment === 'negative' ? acc + 1 : acc), 0),
    );

    setNeutralCount(
      responses.reduce((acc, cur) => (cur.sentiment === 'neutral' ? acc + 1 : acc), 0),
    );
  };

  const { data: survey, isLoading: surveyLoading } = useSurvey({ surveyId });

  const { data: responses, isLoading: responsesLoading } = useQuestionResponses(
    {
      questionId,
      filters:
        selectedAttributeIds.length > 0
          ? {
              ...Object.fromEntries(searchFilters.entries()),
              segment_ids: selectedAttributeIds.join(','),
            }
          : searchFilters,
      surveyId,
    },
    {
      enabled: !!searchFilters,
      onSuccess: (data) => setPercentages(data.data),
    },
  );

  const { data: theme, isLoading: themeLoading } = useThemes(
    { themeIds: [themeId] },
    { select: (data) => data.data[0] },
  );

  const { data: questions, isLoading: questionsLoading } = useQuestions({ surveyId });

  const question = questions?.data.find((q) => q.id === questionId);

  const { data: summary, isLoading: summaryLoading } = useThemeSummary(
    { surveyId, codeFrameId: question?.code_frame_id, themeId },
    { enabled: !!question },
  );

  const { data: segment, isFetching: segmentFetching } = usePersistedSegment(
    { surveyId, segmentId: searchParams.get('segmentId') },
    { enabled: !!searchParams.get('segmentId') },
  );

  useEffect(() => {
    // Filter options sometimes contain special characters like `+` and `&`
    // In order to link to this page and preserve those characters, those params are encoded as `_$PLUS_` and `_$AMPERSAND_`
    const decodedUrlSearchParams = new URLSearchParams({ theme_id: themeId, with_theme_ids: true });
    searchParams.forEach((value, key) => {
      if (key !== 'segmentId') {
        decodedUrlSearchParams.append(
          key,
          value.replaceAll('_$PLUS_', '+').replaceAll('_$AMPERSAND_', '&'),
        );
      }
    });
    setSearchFilters(decodedUrlSearchParams);
  }, [searchParams, themeId]);

  // whenever page changes, scroll back to top
  useEffect(() => {
    window.scroll(0, 0);
  }, [page]);

  useEffect(() => {
    const cached = localStorage.getItem(DISPLAY_ATTRIBUTES_CACHE_KEY);
    setSelectedAttributeIds(cached ? JSON.parse(cached) : []);
  }, [DISPLAY_ATTRIBUTES_CACHE_KEY]);

  useEffect(() => {
    localStorage.setItem(DISPLAY_ATTRIBUTES_CACHE_KEY, JSON.stringify(selectedAttributeIds));
  }, [selectedAttributeIds, DISPLAY_ATTRIBUTES_CACHE_KEY]);

  const afterSearch = useCallback(
    (searchTerm) => {
      if (responses?.data) {
        setPage(1);
        const filteredData = responses.data.filter((r) =>
          r.text.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setPercentages(filteredData);
      }
    },
    [setPage, responses?.data],
  );

  const columns = [
    {
      dataIndex: 'text',
      render: (text, record) => {
        return (
          <Flex vertical gap={6}>
            <StyledResponseText italic>{`"${he.decode(text)}"`}</StyledResponseText>
            <Row gutter={[8, 8]}>
              {record.segment_responses &&
                Object.entries(record.segment_responses).map(([k, v]) => <Tag key={k}>{v}</Tag>)}
            </Row>
          </Flex>
        );
      },
    },
  ];

  const handlePageChange = (newPage, newPageSize) => {
    // if changing page size, return to page 1
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setPage(1);
    } else {
      setPage(newPage);
    }
  };

  const sentimentChartConfig = getThemeSentimentBarChart({
    positiveCount,
    negativeCount,
    neutralCount,
  });

  const sentimentChart = [
    {
      key: 'sentiment',
      label: <Text strong>Sentiment Distribution</Text>,
      children: (
        <ReactECharts
          option={sentimentChartConfig.option}
          style={{ height: sentimentChartConfig.height, width: '100%' }}
          opts={{ renderer: 'svg' }}
        />
      ),
    },
  ];

  if (surveyLoading || themeLoading || questionsLoading || summaryLoading || segmentFetching) {
    return <Loading />;
  }

  return (
    <StyledDiv>
      <BackButton
        onClick={() => navigate(`/surveys/${surveyId}?questionId=${questionId}`)}
        text="Back to themes"
      />
      <Flex vertical gap={12}>
        <Flex align="baseline" gap={16}>
          <Title level={4}>{`Individual responses that contain the theme ${theme.name}`}</Title>
          <Text type="secondary">{segment?.data.label}</Text>
        </Flex>
        {!searchParams.get('segmentId') && summary?.data.summary && (
          <ThemeSummary themeId={themeId} question={question} summary={summary.data} />
        )}
        {question.display_sentiment && <StyledCollapse collapsible="icon" items={sentimentChart} />}
        <StyledSearchableTable
          searchPlaceholder="Search responses..."
          showHeader={false}
          pagination={{
            defaultPageSize: 50,
            onChange: handlePageChange,
            current: page,
            pageSize,
            showTotal: (total) => `Total ${total} responses`,
          }}
          baseData={responses?.data}
          columns={columns}
          rowKey="id"
          searchColumns={['text']}
          afterSearch={afterSearch}
          extra={
            survey.data.upload_type === SurveyUploadType.SURVEY ? (
              <Button type="primary" onClick={() => setAttributesModalVisible(true)}>
                Display attributes
              </Button>
            ) : null
          }
          loading={responsesLoading}
        />
      </Flex>
      <DisplayAttributesModal
        visible={attributesModalVisible}
        setVisible={setAttributesModalVisible}
        attributes={questions.data.filter((q) => q.is_filterable)}
        onOk={setSelectedAttributeIds}
        selectedAttributeIds={selectedAttributeIds}
      />
    </StyledDiv>
  );
}

export default ResponseExplorer;
